import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Upload, Loader2, X, Camera, CheckCircle } from 'lucide-react'
import { WidgetHeader } from './WidgetHeader'
import { createFeedback, uploadFeedbackImage } from '../../lib/feedbackApi'
import type { FeedbackTypeOption } from './WidgetFeedbackType'

const TYPE_LABELS: Record<FeedbackTypeOption, string> = {
  feature: 'Feature request',
  improvement: 'Improvement',
  bug: 'Bug report',
  integration: 'Integration request',
}

const PLACEHOLDER_MAP: Record<FeedbackTypeOption, string> = {
  feature: 'Describe the feature and why you need it...',
  improvement: 'What should we refine or do differently?',
  bug: 'What happened? What did you expect?',
  integration: 'Which tool or service do you want to connect?',
}

interface WidgetFeedbackFormProps {
  feedbackType: FeedbackTypeOption
  initialTitle?: string
  initialDescription?: string
  onBack: () => void
  onClose: () => void
  onSuccess: () => void
}

export function WidgetFeedbackForm({ feedbackType, initialTitle, initialDescription, onBack, onClose, onSuccess }: WidgetFeedbackFormProps) {
  const [title, setTitle] = useState(initialTitle || '')
  const [description, setDescription] = useState(initialDescription || '')
  const [files, setFiles] = useState<File[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Handle paste for image upload
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file && files.length < 3) {
            setFiles((prev) => [...prev, file].slice(0, 3))
          }
          break
        }
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [files.length])

  const addFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return
    const validFiles = Array.from(newFiles).filter(
      (f) => f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024
    )
    setFiles((prev) => [...prev, ...validFiles].slice(0, 3))
  }, [])

  // Create blob URLs and revoke on cleanup
  const fileUrls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files])
  useEffect(() => {
    return () => { fileUrls.forEach((url) => URL.revokeObjectURL(url)) }
  }, [fileUrls])

  const removeFile = (index: number) => {
    URL.revokeObjectURL(fileUrls[index])
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const item = await createFeedback({
        title: title.trim(),
        description: description.trim(),
        type: feedbackType,
        is_anonymous: false,
      })

      // Upload images if any
      for (const file of files) {
        try {
          await uploadFeedbackImage(item.id, file)
        } catch {
          console.warn('Failed to upload image:', file.name)
        }
      }

      setSuccess(true)
      setTimeout(() => {
        onSuccess()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <div className="flex flex-col h-full">
        <WidgetHeader title={TYPE_LABELS[feedbackType]} onClose={onClose} />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-lg font-semibold mb-1">Thank you!</h3>
          <p className="text-sm text-muted-foreground text-center">
            Your feedback has been submitted. We appreciate your input.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <WidgetHeader title={TYPE_LABELS[feedbackType]} onBack={onBack} onClose={onClose} />
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {error && (
          <div className="p-3 text-sm bg-red-500/10 text-red-600 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="Brief summary"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
            rows={4}
            placeholder={PLACEHOLDER_MAP[feedbackType]}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-y"
          />
        </div>

        {/* Screenshots */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Screenshots <span className="text-xs text-muted-foreground">(optional, up to 3)</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {files.map((file, i) => (
              <div key={i} className="relative group">
                <img
                  src={fileUrls[i]}
                  alt={file.name}
                  className="w-16 h-16 object-cover rounded-lg border border-border"
                />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {files.length < 3 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-dashed border-border rounded-lg hover:bg-secondary transition-colors text-muted-foreground"
            >
              {files.length === 0 ? (
                <>
                  <Camera className="w-4 h-4" />
                  <span>Add a screenshot</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Add another</span>
                </>
              )}
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>
      </div>

      {/* Submit button */}
      <div className="px-4 py-3 border-t border-border shrink-0">
        <button
          onClick={handleSubmit}
          disabled={submitting || !title.trim() || !description.trim()}
          className="w-full px-4 py-2.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  )
}
